import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import { Target, ShieldCheck, Loader2, Facebook, Twitter, Link2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRAND_COLORS = ["#1E9E4A", "#E63946", "#F2C14E", "#ffffff"];

const SHARE_URL = "https://2run4hope.org";
const SHARE_TEXT =
  "I just registered for 2Run4Hope! Join me at the start line — every mile funds transitional housing for families in crisis.";
const FACEBOOK_SHARE_URL = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
  SHARE_URL
)}&quote=${encodeURIComponent(SHARE_TEXT)}`;
const X_SHARE_URL = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
  SHARE_TEXT
)}&url=${encodeURIComponent(SHARE_URL)}`;

const fireCelebration = () => {
  const base = { colors: BRAND_COLORS, disableForReducedMotion: true, zIndex: 9999 };
  confetti({ ...base, particleCount: 130, spread: 80, origin: { y: 0.6 } });
  setTimeout(() => {
    confetti({ ...base, particleCount: 90, angle: 60, spread: 60, origin: { x: 0, y: 0.7 } });
  }, 250);
  setTimeout(() => {
    confetti({ ...base, particleCount: 90, angle: 120, spread: 60, origin: { x: 1, y: 0.7 } });
  }, 450);
};

const FORM_CSS = `
  .bloomerang-embed .registration-form {
    font-family: 'Satoshi', sans-serif;
    color: #272D30;
    font-size: 15px;
    line-height: 1.55;
  }
  .bloomerang-embed .registration-form h3 {
    font-family: 'Cabinet Grotesk', sans-serif;
    font-size: 1.3rem;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: #0A0A0A;
    margin: 0 0 1rem;
  }
  .bloomerang-embed .registration-form .section {
    padding: 0 !important;
    margin-bottom: 2.25rem;
  }
  .bloomerang-embed .registration-form .section.registrationType::before {
    content: "Step 1 · Choose Your Race";
  }
  .bloomerang-embed .registration-form .section.contact::before {
    content: "Step 2 · Contact Information";
  }
  .bloomerang-embed .registration-form .section.billing-address::before {
    content: "Step 3 · Billing Address";
  }
  .bloomerang-embed .registration-form .section.payment::before {
    content: "Step 4 · Payment";
  }
  .bloomerang-embed .registration-form .section.registrationType::before,
  .bloomerang-embed .registration-form .section.contact::before,
  .bloomerang-embed .registration-form .section.billing-address::before,
  .bloomerang-embed .registration-form .section.payment::before {
    display: block;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: #1E9E4A;
    margin-bottom: 0.9rem;
  }
  .bloomerang-embed .registration-form .field {
    padding: 0 !important;
    margin-bottom: 1rem;
  }
  .bloomerang-embed .registration-form .field .required-star {
    color: #E63946 !important;
  }
  .bloomerang-embed .registration-form label {
    color: #3f3f46 !important;
    font-weight: 600;
    font-size: 14px;
    margin-bottom: 0.45rem;
  }
  .bloomerang-embed .registration-form .field.radio label {
    display: flex !important;
    align-items: flex-start;
    gap: 0.9rem;
    width: 100%;
    padding: 1rem 1.15rem;
    border: 1.5px solid #E4E4E7;
    border-radius: 1rem;
    background: #ffffff;
    cursor: pointer;
    font-weight: 500;
    color: #272D30 !important;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease, transform 0.2s ease;
  }
  .bloomerang-embed .registration-form .field.radio label:hover {
    border-color: #1E9E4A;
    transform: translateY(-1px);
  }
  .bloomerang-embed .registration-form .field.radio label:has(input:checked) {
    border-color: #1E9E4A;
    background: #F0FAF4;
    box-shadow: 0 0 0 1.5px #1E9E4A;
  }
  .bloomerang-embed .registration-form .field.radio input {
    width: 18px !important;
    height: 18px;
    margin: 2px 0 0 !important;
    flex-shrink: 0;
    accent-color: #1E9E4A;
  }
  .bloomerang-embed .registration-form .field.radio .label {
    font-size: 14.5px;
    line-height: 1.5;
  }
  .bloomerang-embed .registration-form .field input:not([type="radio"]):not([type="checkbox"]):not([type="submit"]),
  .bloomerang-embed .registration-form .field select,
  .bloomerang-embed .registration-form .field textarea {
    max-width: 100% !important;
    width: 100% !important;
    box-sizing: border-box !important;
    padding: 0.8rem 1rem !important;
    border: 1.5px solid #E4E4E7;
    border-radius: 0.9rem;
    background: #ffffff;
    font-size: 15px;
    color: #0A0A0A;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }
  .bloomerang-embed .registration-form .field input:focus,
  .bloomerang-embed .registration-form .field select:focus,
  .bloomerang-embed .registration-form .field textarea:focus {
    outline: none;
    border-color: #1E9E4A;
    box-shadow: 0 0 0 3px rgba(30, 158, 74, 0.16);
  }
  .bloomerang-embed .registration-form .registration-summary #numberOfRegistrations {
    width: 72px !important;
  }
  .bloomerang-embed .registration-form .registration-summary input.amount-field {
    width: 120px !important;
  }
  .bloomerang-embed .registration-form table.registration-summary {
    width: 100%;
    background: #F9FAFB;
    border: 1px solid #E4E4E7;
    border-radius: 1rem;
    border-spacing: 0;
    border-collapse: separate;
    overflow: hidden;
    margin-top: 0.5rem;
  }
  .bloomerang-embed .registration-form .registration-summary th {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.15em;
    color: #71717a;
    padding: 0.85rem 1rem !important;
  }
  .bloomerang-embed .registration-form .registration-summary td {
    padding: 0.85rem 1rem !important;
  }
  .bloomerang-embed .registration-form .registration-summary .summary-total {
    border-top: 1.5px solid #E4E4E7 !important;
    font-family: 'Cabinet Grotesk', sans-serif;
    font-size: 1.05rem;
  }
  .bloomerang-embed .registration-form .field.checkbox {
    border: 1.5px solid #E4E4E7 !important;
    max-width: 100% !important;
    border-radius: 1rem;
    height: auto !important;
    line-height: 1.5 !important;
    padding: 0 !important;
  }
  .bloomerang-embed .registration-form .field.checkbox label {
    display: flex !important;
    align-items: center;
    gap: 0.75rem;
    padding: 0.9rem 1.1rem;
    cursor: pointer;
    width: 100%;
    margin: 0;
  }
  .bloomerang-embed .registration-form .field.checkbox input {
    position: static !important;
    opacity: 1 !important;
    width: 18px !important;
    height: 18px;
    accent-color: #1E9E4A;
    flex-shrink: 0;
  }
  .bloomerang-embed .registration-form .field.checkbox.selected {
    background: #F0FAF4 !important;
    color: #0A0A0A !important;
    border-color: #1E9E4A !important;
  }
  .bloomerang-embed .registration-form #card-element {
    max-width: 100% !important;
    width: 100% !important;
    padding: 0.95rem 1rem !important;
    border: 1.5px solid #E4E4E7 !important;
    border-radius: 0.9rem;
    background: #ffffff;
  }
  .bloomerang-embed .registration-form .errors {
    border: 1.5px solid #E63946 !important;
    color: #E63946 !important;
    border-radius: 0.9rem;
    padding: 0.9rem 1.1rem !important;
    margin-bottom: 1.25rem;
    background: #FEF2F3;
    font-weight: 600;
  }
  .bloomerang-embed .registration-form label.error {
    color: #E63946 !important;
    font-size: 12.5px;
    font-weight: 600;
    padding: 4px 2px 0 !important;
    float: none !important;
  }
  .bloomerang-embed .btn-group {
    margin-top: 0.5rem;
  }
  .bloomerang-embed .btn-submit-registration {
    width: 100%;
    padding: 1.05rem 2rem !important;
    border: none;
    border-radius: 999px;
    background: #1E9E4A;
    color: #ffffff;
    font-family: 'Cabinet Grotesk', sans-serif;
    font-size: 1.05rem;
    font-weight: 700;
    cursor: pointer;
    transition: background-color 0.25s ease, transform 0.25s ease, box-shadow 0.25s ease;
  }
  .bloomerang-embed .btn-submit-registration:hover:not(:disabled) {
    background: #0A0A0A;
    transform: translateY(-2px);
    box-shadow: 0 14px 30px -12px rgba(10, 10, 10, 0.45);
  }
  .bloomerang-embed .btn-submit-registration:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
  .bloomerang-embed .donation-success h2 {
    font-family: 'Cabinet Grotesk', sans-serif;
    font-size: 1.75rem;
    font-weight: 800;
    color: #1E9E4A;
    margin-bottom: 1rem;
  }
  .bloomerang-embed .donation-success p {
    color: #3f3f46;
    line-height: 1.7;
  }
  .bloomerang-embed .additional-tickets button {
    margin-top: 1rem;
    padding: 0.8rem 1.6rem;
    border: none;
    border-radius: 999px;
    background: #1E9E4A;
    color: #fff;
    font-family: 'Cabinet Grotesk', sans-serif;
    font-weight: 700;
    cursor: pointer;
  }
  .bloomerang-embed #registration-processing-container h2 {
    font-family: 'Cabinet Grotesk', sans-serif;
    font-size: 1.5rem;
    font-weight: 800;
    color: #0A0A0A;
  }
  .bloomerang-embed .registration-form .section.captcha {
    display: none !important;
  }
  .bloomerang-embed .registration-form .section.true-impact {
    display: none !important;
  }
  /* Special Rate options stay hidden until a coupon unlocks them */
  .bloomerang-embed .registration-form .section.registrationType .field.radio.special-rate {
    display: none !important;
  }
  .bloomerang-embed.coupon-unlocked .registration-form .section.registrationType .field.radio.special-rate {
    display: block !important;
  }
  .bloomerang-embed.coupon-unlocked .registration-form .section.registrationType .field.radio.standard-rate {
    display: none !important;
  }
  .bloomerang-embed .registration-form .registration-summary th.quantity-column,
  .bloomerang-embed .registration-form .registration-summary .summary-row td:first-child {
    display: none !important;
  }
  @media (max-width: 640px) {
    .bloomerang-embed .registration-form .field.radio label {
      padding: 0.9rem;
    }
  }
`;

export const RegisterWidget = () => {
  const hostRef = useRef(null);
  const pendingAmountRef = useRef(0);
  const [goal, setGoal] = useState(null);
  const [formReady, setFormReady] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [couponOpen, setCouponOpen] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [couponError, setCouponError] = useState(false);
  const [couponUnlocked, setCouponUnlocked] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(`${SHARE_TEXT} ${SHARE_URL}`);
    } catch {
      // clipboard blocked by permissions — still show feedback
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  // Tag the vendor form's rate options once it builds: special-rate twins stay
  // hidden until a coupon unlocks them (then the standard rates hide instead).
  useEffect(() => {
    if (!formReady) return;
    document
      .querySelectorAll(".section.registrationType .field.radio")
      .forEach((field) => {
        const name = field.querySelector("input")?.dataset.shortName || "";
        if (name.endsWith("- Special Rate")) field.classList.add("special-rate");
        else if (name && name !== "Donation") field.classList.add("standard-rate");
      });
  }, [formReady]);

  const applyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    // Validated and usage-tracked on the server, grouped per audience
    let valid = false;
    try {
      const r = await fetch(`${API}/coupon/redeem`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
      valid = r.ok && (await r.json()).valid === true;
    } catch {
      valid = false;
    }
    if (!valid) {
      setCouponError(true);
      return;
    }
    setCouponError(false);
    setCouponUnlocked(true);
    // If a standard rate was already picked, switch it to its Special Rate twin
    const checked = document.querySelector(
      ".section.registrationType input[name='registration-type']:checked"
    );
    if (checked && !(checked.dataset.shortName || "").endsWith("- Special Rate")) {
      const twinName = `${checked.dataset.shortName} - Special Rate`;
      let twin = null;
      document
        .querySelectorAll(".section.registrationType input[name='registration-type']")
        .forEach((input) => {
          if (input.dataset.shortName === twinName) twin = input;
        });
      if (twin) {
        twin.checked = true;
        twin.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }
    confetti({
      colors: BRAND_COLORS,
      particleCount: 60,
      spread: 60,
      origin: { y: 0.5 },
      disableForReducedMotion: true,
      zIndex: 9999,
    });
  };

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    if (!window.__bloomerangFormLoading) {
      window.__bloomerangFormLoading = true;

      const anchor = document.createElement("script");
      anchor.type = "text/javascript";
      anchor.id = "bloomerangForm9279488";
      host.appendChild(anchor);

      const main = document.createElement("script");
      main.type = "text/javascript";
      main.src = `/bloomerang-form.js?v=${Date.now()}`;
      host.appendChild(main);
    }

    const poll = setInterval(() => {
      if (document.getElementById("registration-form")) {
        setFormReady(true);
        clearInterval(poll);
      }
    }, 500);
    const timeout = setTimeout(() => {
      if (!document.getElementById("registration-form")) {
        setLoadError(true);
      }
    }, 30000);
    // Celebrate the moment Bloomerang swaps in the success screen,
    // and move the fundraising bar by the amount just registered
    const successObserver = new MutationObserver(() => {
      if (host.querySelector(".donation-success")) {
        successObserver.disconnect();
        fireCelebration();
        setRegistered(true);
        const amount = pendingAmountRef.current;
        if (amount > 0) {
          fetch(`${API}/goal/add`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ amount }),
          })
            .then((r) => (r.ok ? r.json() : Promise.reject(r)))
            .then(setGoal)
            .catch(() => {});
        }
      }
    });
    successObserver.observe(host, { childList: true, subtree: true });
    // Stash the order total when the visitor hits Register
    const captureAmount = (e) => {
      if (e.target && e.target.id === "express-submit") {
        const txt = document.getElementById("totalPrice")?.textContent || "0";
        pendingAmountRef.current = parseFloat(txt.replace(/[^0-9.]/g, "")) || 0;
      }
    };
    host.addEventListener("click", captureAmount);
    return () => {
      clearInterval(poll);
      clearTimeout(timeout);
      successObserver.disconnect();
      host.removeEventListener("click", captureAmount);
    };
  }, []);

  useEffect(() => {
    fetch(`${API}/goal`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then(setGoal)
      .catch(() => setGoal(null));
  }, []);

  const raised = goal?.raised ?? 0;
  const goalAmt = goal?.goal ?? 50000;
  const pct = goalAmt ? Math.min(100, (raised / goalAmt) * 100) : 0;

  return (
    <section
      id="register-widget"
      data-testid="register-widget-section"
      className="bg-white py-24 md:py-32"
    >
      <style>{FORM_CSS}</style>
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-hope-green">
                Official Registration
              </p>
              <h2
                data-testid="register-widget-heading"
                className="font-heading text-4xl font-black leading-[1.05] tracking-tighter text-hope-ink sm:text-5xl"
              >
                Secure Your Spot
                <br />
                at the Start Line<span className="text-hope-red">.</span>
              </h2>
              <p className="mt-6 max-w-md text-base leading-relaxed text-zinc-600 md:text-lg">
                Register right here — secure checkout, instant confirmation, and
                every dollar funds transitional housing for families in crisis.
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <div
                data-testid="goal-thermometer"
                className="mt-12 rounded-3xl border border-black/5 bg-hope-cream p-8"
              >
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-hope-red/10 text-hope-red">
                    <Target className="h-5 w-5" />
                  </span>
                  <p className="text-sm font-semibold uppercase tracking-[0.2em] text-zinc-500">
                    Fundraising Goal
                  </p>
                </div>
                <div className="mt-6 flex items-baseline gap-2">
                  <span
                    data-testid="goal-raised"
                    className="font-heading text-5xl font-black tracking-tighter text-hope-green"
                  >
                    ${raised.toLocaleString()}
                  </span>
                  <span className="text-lg text-zinc-500">
                    raised of ${goalAmt.toLocaleString()}
                  </span>
                </div>
                <div className="mt-5 h-4 overflow-hidden rounded-full bg-black/10">
                  <motion.div
                    data-testid="goal-bar-fill"
                    className="h-full rounded-full bg-gradient-to-r from-hope-green to-hope-bright"
                    initial={{ width: "0%" }}
                    whileInView={{ width: `${Math.max(pct, 2)}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
                  />
                </div>
                <p className="mt-4 text-sm text-zinc-500" data-testid="goal-caption">
                  Every registration moves the bar — help us fund transitional
                  housing for families in crisis.
                </p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1} className="h-full">
              <div className="flex h-full flex-col rounded-3xl border border-black/5 bg-white p-6 shadow-md md:p-10">
                <div className="mb-8 flex items-center gap-2.5 text-sm text-zinc-500">
                  <ShieldCheck className="h-4 w-4 text-hope-green" />
                  Secure checkout · Card, bank, and donation options
                </div>
                {loadError ? (
                  <div
                    data-testid="registration-form-error"
                    className="flex min-h-[280px] flex-1 flex-col items-center justify-center gap-4 text-center"
                  >
                    <p className="font-heading text-xl font-bold text-hope-ink">
                      The form is taking longer than usual to load
                    </p>
                    <p className="max-w-sm text-sm text-zinc-500">
                      Please refresh the page once. If it still doesn't appear,
                      send us a message below and we'll register you manually.
                    </p>
                    <button
                      onClick={() => window.location.reload()}
                      data-testid="registration-reload-btn"
                      className="rounded-full bg-hope-green px-6 py-3 font-heading text-sm font-bold text-white transition-colors duration-200 hover:bg-hope-ink"
                    >
                      Refresh Page
                    </button>
                  </div>
                ) : (
                  !formReady && (
                    <div
                      data-testid="registration-form-loading"
                      className="flex min-h-[280px] flex-1 items-center justify-center gap-3 text-sm text-zinc-400"
                    >
                      <Loader2 className="h-5 w-5 animate-spin text-hope-green" />
                      Loading secure registration form…
                    </div>
                  )
                )}
                {formReady && !couponUnlocked && (
                  <div data-testid="coupon-box" className="mb-5">
                    {!couponOpen ? (
                      <button
                        type="button"
                        data-testid="coupon-toggle"
                        onClick={() => setCouponOpen(true)}
                        className="text-sm font-medium text-zinc-500 underline decoration-dotted underline-offset-4 transition-colors hover:text-hope-green"
                      >
                        Have a coupon code?
                      </button>
                    ) : (
                      <div className="flex flex-wrap items-center gap-3">
                        <input
                          data-testid="coupon-input"
                          value={couponInput}
                          onChange={(e) => {
                            setCouponInput(e.target.value);
                            setCouponError(false);
                          }}
                          onKeyDown={(e) => e.key === "Enter" && applyCoupon()}
                          placeholder="Enter your code"
                          autoFocus
                          className="w-44 rounded-full border border-black/10 bg-hope-cream/50 px-4 py-2.5 text-sm text-hope-ink placeholder-zinc-400 transition-colors focus:border-hope-green focus:outline-none"
                        />
                        <button
                          type="button"
                          data-testid="coupon-apply-btn"
                          onClick={applyCoupon}
                          className="rounded-full bg-hope-green px-5 py-2.5 font-heading text-sm font-bold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-hope-bright"
                        >
                          Apply
                        </button>
                        {couponError && (
                          <p data-testid="coupon-error" className="w-full text-sm text-hope-red">
                            That code isn&apos;t valid — double-check it and try again.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {couponUnlocked && (
                  <div
                    data-testid="coupon-success"
                    className="mb-5 inline-flex items-center gap-2 rounded-full bg-hope-green/10 px-4 py-2 text-sm font-semibold text-hope-green"
                  >
                    Special rate unlocked — thank you for restoring hope!
                  </div>
                )}
                <div
                  ref={hostRef}
                  data-testid="bloomerang-form-host"
                  data-lenis-prevent
                  className={`bloomerang-embed flex-1 ${couponUnlocked ? "coupon-unlocked" : ""}`}
                />
                {registered && (
                  <motion.div
                    data-testid="share-panel"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    className="mt-8 rounded-2xl border border-hope-green/20 bg-hope-cream/60 p-6 text-center"
                  >
                    <p className="font-heading text-lg font-bold text-hope-ink">
                      You&apos;re in! Now recruit your crew.
                    </p>
                    <p className="mt-1 text-sm text-zinc-600">
                      Every friend who joins means another family closer to home.
                    </p>
                    <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
                      <a
                        data-testid="share-facebook-btn"
                        href={FACEBOOK_SHARE_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 rounded-full bg-hope-green px-5 py-2.5 font-heading text-sm font-bold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-hope-bright"
                      >
                        <Facebook className="h-4 w-4" /> Facebook
                      </a>
                      <a
                        data-testid="share-x-btn"
                        href={X_SHARE_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 rounded-full bg-hope-ink px-5 py-2.5 font-heading text-sm font-bold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5"
                      >
                        <Twitter className="h-4 w-4" /> X
                      </a>
                      <button
                        type="button"
                        data-testid="share-copy-btn"
                        onClick={copyShareLink}
                        className="inline-flex items-center gap-2 rounded-full border-2 border-hope-green px-5 py-2 font-heading text-sm font-bold text-hope-green transition-colors duration-200 hover:bg-hope-green hover:text-white"
                      >
                        <Link2 className="h-4 w-4" />
                        {copied ? "Link copied!" : "Copy link"}
                      </button>
                    </div>
                  </motion.div>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
